import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  getThreadById,
  getThreads,
} from '../../../../../shared/api/magellan/assistant';
import {
  createThread,
  createThreadMessage,
} from '../../../../../shared/api/magellan/assistant';
import type {
  CreateThreadMessageRequestDTO,
  GetThreadByIdResponseDTO,
} from '../../../../../shared/api/magellan/assistant/dto';
import { displayErrorNotification } from '../../../../../shared/components/notifications/errorNotification';
import type { AssistantUserMessage } from '../../../../../shared/entities/AssistantThreadMessage';
import { AssistantThreadMessageRole } from '../../../../../shared/entities/AssistantThreadMessage';
import { useActiveUserQuery } from '../../../../../shared/hooks/useActiveUserQuery.hook';

export function useCreateThread() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: createThread,
    onSuccess(data) {
      navigate(`/response/assistant/${data.id}`);
    },
  });
}

export function useCreateThreadMessage(threadId: number) {
  const { t } = useTranslation(['notification']);
  const {
    data: { user },
  } = useActiveUserQuery();
  const queryClient = useQueryClient();
  const queryKey = [getThreadById.name, threadId];

  return useMutation({
    mutationFn: (payload: CreateThreadMessageRequestDTO) =>
      createThreadMessage({ threadId, payload }),
    onMutate: async newThreadMessageData => {
      await queryClient.cancelQueries({ queryKey });
      const thread =
        queryClient.getQueryData<GetThreadByIdResponseDTO>(queryKey);

      if (!thread) {
        return false;
      }

      const newMessage: AssistantUserMessage = {
        id: 0,
        role: AssistantThreadMessageRole.USER,
        content: [
          {
            type: 'text',
            content: newThreadMessageData.content,
          },
        ],
        owner: user,
        filters: newThreadMessageData.filters ?? null,
      };
      queryClient.setQueryData(queryKey, {
        ...thread,
        messages: [...thread.messages, newMessage],
      });

      return { previousThread: thread };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      queryClient.setQueryData(queryKey, context.previousThread);
      displayErrorNotification(
        t('notification:errors.somethingWentWrong.title'),
        t('notification:errors.somethingWentWrong.message'),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useGetThreadById(threadId: number) {
  return useSuspenseQuery({
    queryKey: [getThreadById.name, threadId],
    queryFn: () => getThreadById(threadId),
  });
}

export function useGetThreads() {
  return useSuspenseQuery({
    queryKey: [getThreads.name],
    queryFn: getThreads,
  });
}
